import { Button } from '@design/components/button/Button';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import { ReactNode } from 'react';

import styles from './MenuBarV2.module.scss';

interface MenuBarV2Item {
  id: ChartMenuBarV2Ids;
  iconName: SvgIconNames;
}

export interface MenuBarV2Locales {
  addData: string;
  marketLeadingIndicators: string;
  insights: string;
}

interface MenuBarV2Props {
  onPrimarySearch: () => void;
  onMenuItemClick: (id: ChartMenuBarV2Ids) => void;
  disabledItems: boolean;
  children?: ReactNode;
  locales: MenuBarV2Locales;
  insightsDropdown?: ReactNode;
  isInsightsDropdownOpen?: boolean;
  onInsightsDropdownOpenChange?: (open: boolean) => void;
}

export type ChartMenuBarV2Ids = 'marketLeadingIndicators' | 'insights';

export const MenuBarV2 = ({
  onPrimarySearch,
  onMenuItemClick,
  disabledItems,
  children,
  locales,
  insightsDropdown,
  isInsightsDropdownOpen,
  onInsightsDropdownOpenChange,
}: MenuBarV2Props) => {
  const items: MenuBarV2Item[] = [
    {
      id: 'marketLeadingIndicators',
      iconName: 'Indicators',
    },
    {
      id: 'insights',
      iconName: 'ExploreNav',
    },
  ];

  const renderMenuItem = (item: MenuBarV2Item) => {
    const button = (
      <Button
        iconName={item.iconName}
        label={locales[item.id]}
        variant="empty"
        onClick={() => {
          if (item.id !== 'insights' || !insightsDropdown) {
            onMenuItemClick(item.id);
          }
        }}
        disabled={disabledItems}
        data-testid={`${item.id}-button`}
      />
    );

    if (item.id === 'insights' && !!insightsDropdown) {
      return (
        <DropdownMenu
          triggerNode={button}
          open={isInsightsDropdownOpen}
          onOpenChange={e => {
            if (!disabledItems) {
              return onInsightsDropdownOpenChange?.(e);
            }
          }}
          align="start"
          modal={false}
        >
          {insightsDropdown}
        </DropdownMenu>
      );
    }

    return button;
  };

  return (
    <div className={styles.menuBarRoot} data-testid="menu-bar">
      {children && <div className={styles.menuItem}>{children}</div>}
      <div className={clsx(styles.menuItem, styles.menuItemSearch)}>
        <Button
          data-testid="search-button"
          iconName="Add"
          label={locales.addData}
          variant="empty"
          fullWidth
          aria-label={locales.addData}
          onClick={onPrimarySearch}
        />
      </div>
      {items.map(item => (
        <div className={styles.menuItem} key={item.iconName}>
          {renderMenuItem(item)}
        </div>
      ))}
    </div>
  );
};
